import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { motion } from "framer-motion";
import React from "react";
import { match } from "ts-pattern";
import { API, APIConfig, LoginData } from "../../API";
import { usePermissionDeniedModal } from "../../hooks/usePermissionDeniedModal";
import { CaregiverMobile } from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { useLongPress } from "../../utils/useLongPress";
import { If } from "../Generic/If";
import { useMfDevTools } from "../OnboardingSection/hooks/useMfDevTools";
import PermissionDeniedModal from "../PermissionDeniedModal";
import AuthParticles from "./components/AuthParticles";
import DevToolsBottomSheet from "./components/DevToolsBottomSheet";
import EnterMobileFormStage from "./components/stages/EnterMobileFormStage";
import EnterSMSCodeStage from "./components/stages/EnterSMSCodeStage";
import LoggedInStage from "./components/stages/LoggedInStage";
import PassthroughStage from "./components/stages/PassthroughStage";
import TakePhotoStage from "./components/stages/TakePhotoStage";
import VerticalLogo from "./components/VerticalLogo";

interface Props {
  onLogin: (api: API) => void;
  onVersionOutdated: () => void;
  onUserLocked: (phoneNumber: string) => void;
  onComplete: (params: {
    api: API;
    caregiver: CaregiverMobile;
    isNewCaregiver: boolean;
    shouldSkipPromo: boolean;
  }) => void;
}

type AuthStage =
  | {
      name: "EnterMobile";
    }
  | {
      name: "EnterSMSCode";
      phonenumber: string;
    }
  | {
      name: "LoggedIn";
      loginData: LoginData;
      phonenumber: string | null;
    }
  | {
      name: "TakePhoto";
      caregiver: CaregiverMobile;
      isNewCaregiver: boolean;
      api: API;
    }
  | {
      name: "Passthrough";
      apiConfig: APIConfig;
      refreshToken: string;
    };

function useAuthStage() {
  const [stage, setStage] = React.useState<AuthStage>({ name: "EnterMobile" });

  return {
    stage,
    setStage,
  };
}

function useOverlayKeyboard(params: { enabled: boolean }) {
  const [isOpen, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!params.enabled) {
      return;
    }

    Keyboard.setResizeMode({ mode: KeyboardResize.Ionic });

    const open = () => setOpen(true);
    const close = () => setOpen(false);

    if (document.activeElement !== null) {
      open();
    }

    Keyboard.addListener("keyboardWillShow", open);
    Keyboard.addListener("keyboardWillHide", close);

    return () => {
      Keyboard.removeAllListeners();
      Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    };
  }, [params.enabled]);

  return isOpen;
}

const AuthScreen = (props: Props) => {
  const [animate] = useAutoAnimate<HTMLDivElement>();
  const { stage, setStage } = useAuthStage();
  const { apiConfig, ...mfDevTools } = useMfDevTools();
  const logoRef = React.useRef<HTMLDivElement>(null);
  const permissionDeniedModal = usePermissionDeniedModal();

  useLongPress(logoRef, { duration: 5000, callback: mfDevTools.open });

  const isKeyboardOpen = useOverlayKeyboard({
    enabled: Capacitor.getPlatform() === "ios",
  });

  const handleValidConfirmDetails = (params: { api: API; caregiver: CaregiverMobile }) => {
    props.onLogin(params.api);

    setStage({
      name: "TakePhoto",
      api: params.api,
      caregiver: params.caregiver,
      isNewCaregiver: false,
    });
  };

  const handleValidRegistration = (params: { caregiver: CaregiverMobile; api: API }) => {
    props.onLogin(params.api);

    setStage({
      name: "TakePhoto",
      api: params.api,
      caregiver: params.caregiver,
      isNewCaregiver: true,
    });
  };

  const handleLoginFromPassthrough: React.ComponentPropsWithRef<
    typeof PassthroughStage
  >["onLogin"] = (params) => {
    props.onLogin(params.api);
    props.onComplete({
      api: params.api,
      caregiver: params.caregiver,
      isNewCaregiver: false,
      shouldSkipPromo: true,
    });
  };

  return (
    <AuthScreen.Styled>
      <AuthScreen.Wrapper shouldPushUp={isKeyboardOpen} ref={animate}>
        <VerticalLogo ref={logoRef} />
        <If condition={mfDevTools.isButtonVisible}>
          <AuthScreen.DevToolsButton onClick={mfDevTools.open}>!</AuthScreen.DevToolsButton>
        </If>

        <If condition={stage.name === "EnterMobile"}>
          <AuthScreen.TagLine>
            {/* {translate(STRINGS.THE_BEST_PLACE_FOR_CAREGIVERS)} */}
          </AuthScreen.TagLine>
        </If>

        {match(stage)
          .with({ name: "EnterMobile" }, () => (
            <EnterMobileFormStage
              apiConfig={apiConfig}
              onValidSubmit={(x) => setStage({ name: "EnterSMSCode", phonenumber: x.phonenumber })}
              onVersionOutdated={props.onVersionOutdated}
            />
          ))
          .with({ name: "EnterSMSCode" }, ($stage) => (
            <EnterSMSCodeStage
              apiConfig={apiConfig}
              phoneNumber={$stage.phonenumber}
              onClickChangePhoneNumber={() => setStage({ name: "EnterMobile" })}
              onValidSubmit={(loginData) =>
                setStage({
                  name: "LoggedIn",
                  loginData,
                  phonenumber: $stage.phonenumber,
                })
              }
              onVersionOutdated={props.onVersionOutdated}
              onUserLocked={props.onUserLocked}
            />
          ))
          .with({ name: "LoggedIn" }, ($stage) => (
            <LoggedInStage
              apiConfig={apiConfig}
              loginData={$stage.loginData}
              phonenumber={$stage.phonenumber}
              onVersionOutdated={props.onVersionOutdated}
              onValidConfirmDetails={handleValidConfirmDetails}
              onValidRegistration={handleValidRegistration}
            />
          ))
          .with({ name: "TakePhoto" }, ($stage) => (
            <TakePhotoStage
              api={$stage.api}
              caregiver={$stage.caregiver}
              onClickNext={() =>
                props.onComplete({
                  api: $stage.api,
                  caregiver: $stage.caregiver,
                  isNewCaregiver: $stage.isNewCaregiver,
                  shouldSkipPromo: true,
                })
              }
            />
          ))
          .with({ name: "Passthrough" }, ($stage) => (
            <PassthroughStage
              apiConfig={$stage.apiConfig}
              refreshJWT={$stage.refreshToken}
              onInvalidLogin={() => setStage({ name: "EnterMobile" })}
              onUserLocked={props.onUserLocked}
              onVersionOutdated={props.onVersionOutdated}
              onLogin={handleLoginFromPassthrough}
              onUserNotRegistered={(loginData) =>
                setStage({ name: "LoggedIn", loginData, phonenumber: null })
              }
            />
          ))
          .exhaustive()}

        <DevToolsBottomSheet
          {...mfDevTools.props}
          onLoginViaAuthToken={(refreshToken) =>
            setStage({ name: "Passthrough", apiConfig, refreshToken })
          }
        />
      </AuthScreen.Wrapper>
      <AuthParticles />
      <PermissionDeniedModal
        state={permissionDeniedModal.state}
        onClose={permissionDeniedModal.closeModal}
      />
    </AuthScreen.Styled>
  );
};

AuthScreen.TagLine = styled.p`
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
`;

AuthScreen.Stages = styled(motion.div)``;

AuthScreen.AuthFormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 48px;
`;

AuthScreen.DevToolsButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: #ffcf32;
  border: none;
  color: black;
  font-weight: 700;
  font-size: 20px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const authScreenIntro = keyframes`
    from { opacity: 0; transform: translateY(20px) }
`;

AuthScreen.Styled = styled.div`
  background-color: #fff;
  height: 100%;
  animation: ${authScreenIntro} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both 500ms;
`;

AuthScreen.Wrapper = styled.div<{ shouldPushUp: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 56px;
  position: relative;
  z-index: 1;
  transition: all 250ms ease-in-out;

  ${(props) => {
    if (props.shouldPushUp) {
      return css`
        transform: translateY(-20vh);
      `;
    }

    return;
  }}

  ${VerticalLogo.Styled} {
    flex-direction: row;
    align-items: center;
    justify-content: center;

    gap: 12px;

    .AppIcon {
      width: auto;
      max-width: 128px;
    }

    .AppIcon:first-of-type {
      height: 40px;
      width: 40px;
    }
  }

  @media (max-width: 375px) {
    padding: 0 32px;
  }
`;

export default AuthScreen;
